import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { TellentHttpApiService } from '@core/tellent-http-legacy';
import { injectIsNativeMobileHr } from '@core/tellent-mobile-contracts';
import type {
  TellentCurrentUser,
  TellentCurrentUserUpdate,
  TellentPasswordChange,
  TellentPasswordChangeResponse,
  TellentUserResponse,
} from '@core/tellent-types';

@Injectable({ providedIn: 'root' })
export class TellentCurrentUserHttpClient {
  private readonly isNativeMobileHr = injectIsNativeMobileHr();
  protected readonly httpApi = inject(TellentHttpApiService);

  public fetchCurrentUser(): Observable<TellentCurrentUser> {
    return this.httpApi.get('/api/me', null, { skipCompany: true }).pipe(
      map((response: TellentUserResponse) => ({
        ...response.user,
        isAuthorizedWith: response.accessToken.kind,
      })),
    );
  }

  public updatePassword(payload: TellentPasswordChange): Observable<TellentPasswordChangeResponse> {
    return this.httpApi.patch('/api/me/update-password', null, payload, { skipCompany: true });
  }

  public updateCurrentUserData(
    payload: Partial<TellentCurrentUserUpdate>,
  ): Observable<TellentCurrentUser> {
    const formData = new FormData();

    Object.entries(payload).forEach(
      ([key, value]: [string, string | boolean | File | undefined]) => {
        // eslint-disable-next-line eqeqeq
        if (value == undefined) return;

        const isFile = value instanceof File;

        if (key === 'recruiteeLanguage' && !isFile) {
          formData.append(`appLanguages[recruitment]`, `${value}`);
        } else if (key === 'kiwiLanguage' && !isFile) {
          formData.append(`appLanguages[coreHr]`, `${value}`);
        } else {
          formData.append(`${key}`, isFile ? <File>value : `${value}`);
        }
      },
    );

    if (this.isNativeMobileHr) {
      // Workaround for https://github.com/ionic-team/capacitor/issues/7579 - to be removed once we upgrade to Capacitor 8 (currently in alpha)
      return this.httpApi
        .patch('/api/me/update-profile', null, formData, {
          headers: {
            'Content-Type': `multipart/form-data; boundary=----WebKitFormBoundary${Math.random().toString(36).substring(2, 15)}`,
          },
          skipCompany: true,
        })
        .pipe(map((response: TellentUserResponse) => response.user));
    } else {
      return this.httpApi
        .patch('/api/me/update-profile', null, formData, { skipCompany: true })
        .pipe(map((response: TellentUserResponse) => response.user));
    }
  }

  public leaveCompany(orgGuid: string): Observable<TellentCurrentUser> {
    return this.httpApi
      .delete(`/api/org/${orgGuid}/leave`, null, { skipCompany: true })
      .pipe(map((response: TellentUserResponse) => response.user));
  }

  public deleteAccount(code: string): Observable<{ success: boolean }> {
    return this.httpApi.post(`/api/me/request-deletion`, null, { code }, { skipCompany: true });
  }

  public trackMau(appGuid: string): Observable<any> {
    return this.httpApi.post(`/api/app/${appGuid}/analytics/visits`, null, null, {
      skipCompany: true,
    });
  }

  public trackMauSettings(orgGuid: string): Observable<any> {
    return this.httpApi.post(`/api/org/${orgGuid}/analytics/visits`, null, null, {
      skipCompany: true,
    });
  }
}
